/**
 * Decodes percent-encoded bracket characters in a query string so that
 * `URLSearchParams` output like `filter%5Bname%5D` becomes `filter[name]`.
 */
export const decodeBrackets = (query: string): string =>
  query.replace(/%5B/gi, '[').replace(/%5D/gi, ']');

/**
 * Returns the matching enum value from a URL query param, or null if missing/invalid.
 *
 * @example
 * enum DurationEnum { day = 'day', week = 'week' }
 * getEnumParam(DurationEnum, 'day') // DurationEnum.day
 * getEnumParam(DurationEnum, 'century') // null
 */
export const getEnumParam = <T extends Record<string, string>>(
  enumObj: T,
  value: string | undefined
): T[keyof T] | null => {
  if (!value) return null;
  const values = new Set(Object.values(enumObj));
  return values.has(value) ? (value as T[keyof T]) : null;
};

/**
 * Returns an array of matched enum values from a comma-separated URL query param,
 * or null if missing/empty. Invalid items are silently dropped.
 *
 * @example
 * enum ActionType { first_action = 'first_action', second_action = 'second_action', }
 * getEnumListParam(ActionType, 'first_action,second_action') // [ActionType.first_action, ActionType.second_action]
 * getEnumListParam(ActionType, 'some_action,second_action') // [ActionType.second_action]
 * getEnumListParam(ActionType, 'custom_action') // null
 * getEnumListParam(ActionType, undefined) // null
 */
export const getEnumListParam = <T extends Record<string, string>>(
  enumObj: T,
  value: string | undefined
): T[keyof T][] | null => {
  if (!value?.trim()) return null;

  const parsed = value
    .split(',')
    .map((item) => getEnumParam(enumObj, item.trim()))
    .filter((item): item is T[keyof T] => item !== null);

  return parsed.length > 0 ? parsed : null;
};

/**
 * Converts a comma-separated URL query param into a clean array of strings,
 * or returns null if the param is missing, empty, or purely whitespace.
 *
 * @example
 * getStringListParam('value1,,value7') // ["value1", "value7"]
 * getStringListParam(', ,') // null
 */
export const getStringListParam = (value: string | undefined): string[] | null => {
  if (!value || !value.trim()) return null;

  const parsed = value
    .split(',')
    .map((item) => item.trim())
    .filter(Boolean);

  return parsed.length > 0 ? parsed : null;
};
